import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, MenuItem, Paper, TextField,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, CircularProgress, Collapse, Select, FormControl, InputLabel,
  Checkbox, ListItemText, OutlinedInput, Autocomplete
} from '@mui/material';
import {
  ShoppingCart, Search, Refresh, PictureAsPdf,
  FilterList, Clear, TableChart
} from '@mui/icons-material';
import axios from 'axios';
import { exportGenericWorkbook } from '../utils/excel/genericWorkbook';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TIPO_OPTIONS = [
  { value: 'compra',       label: 'Compras' },
  { value: 'orden_compra', label: 'Órdenes de Compra' },
  { value: 'requerimiento',label: 'Requerimientos' },
  { value: 'nc_compra',    label: 'N/C de Compras' },
];

const ESTADO_OPTIONS = [
  { value: 'ACTIVO',    label: '✓ Activo' },
  { value: 'ANULADO',   label: '🗑️ Anulado' },
  { value: 'PENDIENTE', label: '⏳ Pendiente' },
  { value: 'APROBADO',  label: '✅ Aprobado' },
  { value: 'RECHAZADO', label: '✗ Rechazado' },
  { value: 'BORRADOR',  label: '📝 Borrador' },
  { value: 'RECIBIDO',  label: '📦 Recibido' },
];

const renderEstado = (doc) => {
  const { estado, origen } = doc;
  const meta = {
    ACTIVO:    { color: 'success', label: '✓ Activo' },
    ANULADO:   { color: 'error',   label: '🗑️ Anulado' },
    PENDIENTE: { color: 'warning', label: '⏳ Pendiente' },
    APROBADO:  { color: 'success', label: '✅ Aprobado' },
    RECHAZADO: { color: 'error',   label: '✗ Rechazado' },
    BORRADOR:  { color: 'default', label: '📝 Borrador' },
    RECIBIDO:  { color: 'info',    label: '📦 Recibido' },
    PARCIAL:   { color: 'warning', label: '🔄 Parcial' },
  }[estado] || { color: 'default', label: estado || '-' };

  if (origen === 'requerimiento') {
    const rMeta = {
      PENDIENTE: { color: 'warning', label: '⏳ Pendiente' },
      APROBADO:  { color: 'success', label: '✅ Aprobado' },
      RECHAZADO: { color: 'error',   label: '✗ Rechazado' },
      ATENDIDO:  { color: 'info',    label: '📦 Atendido' },
    }[estado] || { color: 'default', label: estado || '-' };
    return <Chip label={rMeta.label} color={rMeta.color} variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
  }

  return <Chip label={meta.label} color={meta.color} variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
};

const tipoColor = {
  'Compra':          '#e3f2fd',
  'Orden de Compra': '#fff8e1',
  'Requerimiento':   '#f3e5f5',
  'N/C Compra':      '#fce4ec',
};

const hoy = new Date().toLocaleDateString('en-CA');
const primerDiaMes = new Date(new Date().setDate(1)).toISOString().slice(0, 10);

// ─── Componente principal ─────────────────────────────────────────────────────

const AdminComprasPage = () => {
  const [filtros, setFiltros] = useState({
    tipos:       [],
    estados:     [],
    desde:       primerDiaMes,
    hasta:       hoy,
    proveedor:   '',
    numero:      '',
    sucursalId:  '',
    sucursalObj: null,
  });
  const [sucursales, setSucursales] = useState([]);
  const [showFiltros, setShowFiltros] = useState(true);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    axios.get('/api/sucursales/mis-sucursales').then(r => setSucursales(r.data || [])).catch(() => {});
  }, []);

  const buildParams = useCallback((p = page) => {
    const params = { page: p, limit: 50 };
    if (filtros.tipos.length === 1)   params.tipo      = filtros.tipos[0];
    if (filtros.estados.length === 1) params.estado    = filtros.estados[0];
    if (filtros.desde)     params.desde     = filtros.desde;
    if (filtros.hasta)     params.hasta     = filtros.hasta;
    if (filtros.proveedor) params.proveedor = filtros.proveedor;
    if (filtros.numero)    params.numero    = filtros.numero;
    if (filtros.sucursalId) params.sucursalId = filtros.sucursalId;
    return params;
  }, [filtros, page]);

  const fetchDocs = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = buildParams(p);
      if (filtros.tipos.length > 1) {
        const results = await Promise.all(
          filtros.tipos.map(t => axios.get('/api/compras/documentos', { params: { ...params, tipo: t } }))
        );
        let allDocs = results.flatMap(r => r.data.data || []);
        if (filtros.estados.length > 0) {
          allDocs = allDocs.filter(d => filtros.estados.includes(d.estado));
        }
        allDocs.sort((a, b) => (b.fecha > a.fecha ? 1 : b.fecha < a.fecha ? -1 : b.id - a.id));
        setDocs(allDocs);
        setTotal(results.reduce((s, r) => s + (r.data.total || 0), 0));
      } else {
        const res = await axios.get('/api/compras/documentos', { params });
        let data = res.data.data || [];
        if (filtros.estados.length > 1) {
          data = data.filter(d => filtros.estados.includes(d.estado));
        }
        setDocs(data);
        setTotal(res.data.total || 0);
      }
    } catch (e) {
      setDocs([]);
      console.error(e);
    }
    setLoading(false);
  }, [filtros, page, buildParams]);

  useEffect(() => { fetchDocs(page); }, [fetchDocs, page]);

  const handleTipos = (e) => {
    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    setFiltros(prev => ({ ...prev, tipos: val }));
    setPage(1);
  };

  const handleEstados = (e) => {
    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    setFiltros(prev => ({ ...prev, estados: val }));
    setPage(1);
  };

  const handleFiltro = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const limpiarFiltros = () => {
    setFiltros({ tipos: [], estados: [], desde: primerDiaMes, hasta: hoy, proveedor: '', numero: '', sucursalId: '', sucursalObj: null });
    setPage(1);
  };

  // ─ PDF ───────────────────────────────────────────────────────────────────

  const abrirPdf = (doc) => {
    const tk = localStorage.getItem('token') || '';
    const rutas = {
      compra:        `/api/reporte-pdf/compra/${doc.id}?formato=A4&mode=inline&token=${tk}`,
      orden_compra:  `/api/reporte-pdf/orden-compra/${doc.id}?formato=A4&mode=inline&token=${tk}`,
      requerimiento: `/api/reporte-pdf/requerimiento/${doc.id}?formato=A4&mode=inline&token=${tk}`,
    };
    const url = rutas[doc.origen];
    if (url) window.open(url, '_blank');
    else alert('PDF no disponible para este tipo de documento.');
  };

  const tienePdf = (doc) => ['compra', 'orden_compra', 'requerimiento'].includes(doc.origen);

  // ─ Excel ─────────────────────────────────────────────────────────────────

  const exportarExcel = async () => {
    setExportando(true);
    try {
      let allDocs = [];
      if (filtros.tipos.length > 1) {
        const params = buildParams(1);
        const results = await Promise.all(
          filtros.tipos.map(t => axios.get('/api/compras/documentos', { params: { ...params, limit: 5000, page: 1, tipo: t } }))
        );
        allDocs = results.flatMap(r => r.data.data || []);
        allDocs.sort((a, b) => (b.fecha > a.fecha ? 1 : b.fecha < a.fecha ? -1 : b.id - a.id));
      } else {
        const params = buildParams(1);
        const res = await axios.get('/api/compras/documentos', { params: { ...params, limit: 5000, page: 1 } });
        allDocs = res.data.data || [];
        if (filtros.estados.length > 1) allDocs = allDocs.filter(d => filtros.estados.includes(d.estado));
      }

      const tiposFiltroLabel = filtros.tipos.length
        ? filtros.tipos.map(t => TIPO_OPTIONS.find(o => o.value === t)?.label || t).join(', ')
        : 'Todos';
      const estadosFiltroLabel = filtros.estados.length ? filtros.estados.join(', ') : 'Todos';
      const filtroTexto = `Tipos: ${tiposFiltroLabel} | Estados: ${estadosFiltroLabel} | ${filtros.desde || '---'} a ${filtros.hasta || '---'}`;

      await exportGenericWorkbook({
        titulo: 'CENTRO DE COMPRAS — DOCUMENTOS',
        sheetName: 'CentroCompras',
        tableName: 'tblCentroCompras',
        fileName: 'CentroCompras',
        searchText: filtroTexto,
        usuario: (() => { try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.username || ''; } catch { return ''; } })(),
        columns: [
          { key: 'fecha',         label: 'Fecha',      width: 14 },
          { key: 'tipo_doc',      label: 'Tipo',       width: 20 },
          { key: 'numero_doc',    label: 'Número',     width: 18 },
          { key: 'proveedor',     label: 'Proveedor',  width: 40 },
          { key: 'ruc_proveedor', label: 'RUC',        width: 14 },
          { key: 'total',         label: 'Total',      width: 14, sumTotal: true },
          { key: 'moneda',        label: 'Moneda',     width: 10 },
          { key: 'estado',        label: 'Estado',     width: 16, isEstado: true },
        ],
        rows: allDocs.map(d => ({
          ...d,
          total: d.origen !== 'requerimiento' && d.total > 0 ? parseFloat(d.total) : 0,
        })),
      });
    } catch (e) {
      alert('Error al exportar: ' + e.message);
    }
    setExportando(false);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  const tiposLabel = filtros.tipos.length
    ? filtros.tipos.map(v => TIPO_OPTIONS.find(o => o.value === v)?.label || v).join(', ')
    : 'Todos los tipos';

  const estadosLabel = filtros.estados.length
    ? filtros.estados.join(', ')
    : 'Todos los estados';

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <ShoppingCart color="warning" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Centro de Compras</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {total} documentos
        </Typography>
        <Button size="small" variant="outlined" color="success"
          startIcon={exportando ? <CircularProgress size={14} color="inherit" /> : <TableChart />}
          onClick={exportarExcel} disabled={exportando || loading}>
          Excel
        </Button>
        <Button size="small" variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFiltros(v => !v)}>
          {showFiltros ? 'Ocultar filtros' : 'Filtros'}
        </Button>
        <IconButton onClick={() => fetchDocs(page)} disabled={loading} size="small">
          <Refresh />
        </IconButton>
      </Box>

      {/* Filtros */}
      <Collapse in={showFiltros}>
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>

            <Autocomplete size="small" sx={{ minWidth: 170, flex: '1 1 170px' }}
              options={sucursales}
              getOptionLabel={o => o.nombre || ''}
              value={filtros.sucursalObj}
              onChange={(_, v) => { setFiltros(f => ({ ...f, sucursalId: v?.id || '', sucursalObj: v })); setPage(1); }}
              renderInput={p => <TextField {...p} label="Sucursal" />}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
            />

            <FormControl size="small" sx={{ minWidth: 240, flex: '2 1 240px' }}>
              <InputLabel id="tipo-label">Tipo documento</InputLabel>
              <Select
                labelId="tipo-label"
                multiple
                value={filtros.tipos}
                onChange={handleTipos}
                input={<OutlinedInput label="Tipo documento" />}
                renderValue={() => tiposLabel}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}>
                {TIPO_OPTIONS.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Checkbox checked={filtros.tipos.includes(t.value)} size="small" />
                    <ListItemText primary={t.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, flex: '2 1 200px' }}>
              <InputLabel id="estado-label">Estado</InputLabel>
              <Select
                labelId="estado-label"
                multiple
                value={filtros.estados}
                onChange={handleEstados}
                input={<OutlinedInput label="Estado" />}
                renderValue={() => estadosLabel}
                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}>
                {ESTADO_OPTIONS.map(e => (
                  <MenuItem key={e.value} value={e.value}>
                    <Checkbox checked={filtros.estados.includes(e.value)} size="small" />
                    <ListItemText primary={e.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Desde" type="date" size="small" sx={{ minWidth: 135 }}
              InputLabelProps={{ shrink: true }}
              value={filtros.desde} onChange={e => handleFiltro('desde', e.target.value)} />
            <TextField label="Hasta" type="date" size="small" sx={{ minWidth: 135 }}
              InputLabelProps={{ shrink: true }}
              value={filtros.hasta} onChange={e => handleFiltro('hasta', e.target.value)} />

            <TextField label="Proveedor / RUC" size="small" sx={{ minWidth: 200, flex: '2 1 200px' }}
              value={filtros.proveedor} onChange={e => handleFiltro('proveedor', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchDocs(1)} />

            <TextField label="N° doc" size="small" sx={{ minWidth: 110 }}
              value={filtros.numero} onChange={e => handleFiltro('numero', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchDocs(1)} />

            <Button variant="contained" size="small" color="warning"
              onClick={() => fetchDocs(1)}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Search />}
              disabled={loading}>
              Buscar
            </Button>
            <Tooltip title="Limpiar filtros">
              <IconButton onClick={limpiarFiltros} size="small"><Clear /></IconButton>
            </Tooltip>

          </Box>
        </Paper>
      </Collapse>

      {/* Tabla */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'warning.main' }}>
              {['Fecha', 'Tipo', 'Número', 'Proveedor', 'RUC', 'Total', 'Estado', 'PDF'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} color="warning" />
                </TableCell>
              </TableRow>
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No se encontraron documentos con los filtros seleccionados.
                </TableCell>
              </TableRow>
            ) : docs.map((doc, i) => (
              <TableRow key={`${doc.origen}-${doc.id}`} hover
                sx={{ bgcolor: i % 2 === 0 ? 'inherit' : 'action.hover' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ lineHeight: 1.3 }}>
                    <div>{doc.fecha?.substring(0, 10)}</div>
                    {doc.hora && <div style={{ fontSize: 11, color: '#888' }}>{doc.hora}</div>}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={doc.tipo_doc} size="small"
                    sx={{ bgcolor: tipoColor[doc.tipo_doc] || '#f5f5f5', fontWeight: 600, fontSize: 11 }} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontWeight: 600 }}>
                  {doc.numero_doc}
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={doc.proveedor || ''}><span>{doc.proveedor || '-'}</span></Tooltip>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{doc.ruc_proveedor || '-'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {doc.origen !== 'requerimiento' && doc.total > 0
                    ? `${doc.moneda === 'USD' ? '$' : 'S/'} ${parseFloat(doc.total).toFixed(2)}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {renderEstado(doc)}
                </TableCell>
                <TableCell>
                  {tienePdf(doc) ? (
                    <Tooltip title="Ver PDF">
                      <IconButton size="small" color="error" onClick={() => abrirPdf(doc)}>
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="PDF no disponible">
                      <span>
                        <IconButton size="small" disabled>
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Mostrando {docs.length} de {total} documentos
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <Typography variant="caption">Pág. {page}</Typography>
          <Button size="small" disabled={docs.length < 50} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminComprasPage;
